/**
 * Company Permission Hook
 * ISO 27001 A.9.2.3 - Management of privileged access rights
 *
 * Hook pour vérifier les permissions au niveau company côté frontend
 */

import React, { useCallback, useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';

/**
 * Actions possibles dans l'application
 * Doit correspondre au backend company-permission.middleware.ts
 */
export type CompanyAction =
  | 'create:invoice'
  | 'read:invoice'
  | 'update:invoice'
  | 'delete:invoice'
  | 'create:quote'
  | 'read:quote'
  | 'update:quote'
  | 'delete:quote'
  | 'create:credit-note'
  | 'read:credit-note'
  | 'update:credit-note'
  | 'delete:credit-note'
  | 'create:client'
  | 'read:client'
  | 'update:client'
  | 'delete:client'
  | 'create:product'
  | 'read:product'
  | 'update:product'
  | 'delete:product'
  | 'create:expense'
  | 'read:expense'
  | 'update:expense'
  | 'delete:expense'
  | 'approve:expense'
  | 'read:accounting'
  | 'export:accounting'
  | 'manage:vat'
  | 'read:treasury'
  | 'manage:settings'
  | 'manage:users'
  | 'manage:company';

/**
 * Rôles possibles dans une company
 */
export type CompanyRole =
  | 'OWNER'
  | 'ADMIN'
  | 'MANAGER'
  | 'ACCOUNTANT'
  | 'MEMBER'
  | 'VIEWER';

/**
 * Matrice des permissions par rôle
 * DOIT être synchronisée avec le backend
 */
const PERMISSION_MATRIX: Record<CompanyRole, CompanyAction[]> = {
  OWNER: [
    'create:invoice', 'read:invoice', 'update:invoice', 'delete:invoice',
    'create:quote', 'read:quote', 'update:quote', 'delete:quote',
    'create:credit-note', 'read:credit-note', 'update:credit-note', 'delete:credit-note',
    'create:client', 'read:client', 'update:client', 'delete:client',
    'create:product', 'read:product', 'update:product', 'delete:product',
    'create:expense', 'read:expense', 'update:expense', 'delete:expense', 'approve:expense',
    'read:accounting', 'export:accounting', 'manage:vat', 'read:treasury',
    'manage:settings', 'manage:users', 'manage:company',
  ],
  ADMIN: [
    'create:invoice', 'read:invoice', 'update:invoice', 'delete:invoice',
    'create:quote', 'read:quote', 'update:quote', 'delete:quote',
    'create:credit-note', 'read:credit-note', 'update:credit-note', 'delete:credit-note',
    'create:client', 'read:client', 'update:client', 'delete:client',
    'create:product', 'read:product', 'update:product', 'delete:product',
    'create:expense', 'read:expense', 'update:expense', 'delete:expense', 'approve:expense',
    'read:accounting', 'export:accounting', 'manage:vat', 'read:treasury',
    'manage:settings', 'manage:users',
  ],
  MANAGER: [
    'create:invoice', 'read:invoice', 'update:invoice', 'delete:invoice',
    'create:quote', 'read:quote', 'update:quote', 'delete:quote',
    'create:credit-note', 'read:credit-note', 'update:credit-note',
    'create:client', 'read:client', 'update:client', 'delete:client',
    'create:product', 'read:product', 'update:product', 'delete:product',
    'create:expense', 'read:expense', 'update:expense', 'delete:expense',
  ],
  ACCOUNTANT: [
    'read:invoice', 'read:quote', 'read:credit-note',
    'read:client', 'read:product',
    'read:expense', 'approve:expense',
    'read:accounting', 'export:accounting', 'manage:vat', 'read:treasury',
  ],
  MEMBER: [
    'create:quote', 'read:quote', 'update:quote',
    'create:expense', 'read:expense',
    'read:client', 'read:product',
  ],
  VIEWER: [
    'read:invoice', 'read:quote', 'read:credit-note',
    'read:client', 'read:product', 'read:expense',
  ],
};

/**
 * Hook principal pour les permissions
 */
export function usePermission() {
  const { user } = useAuthStore();

  // Déterminer le rôle effectif
  const effectiveRole = useMemo<CompanyRole | null>(() => {
    if (!user) return null;

    // OWNER ou ADMIN tenant a tous les droits
    if (user.role === 'OWNER' || user.role === 'SUPERADMIN') {
      return 'OWNER';
    }

    // Sinon utiliser le companyRole s'il existe
    if (user.companyRole) {
      return user.companyRole as CompanyRole;
    }

    // Si ADMIN tenant mais pas de companyRole explicite
    if (user.role === 'ADMIN') {
      return 'ADMIN';
    }

    // Par défaut MEMBER
    return 'MEMBER';
  }, [user]);

  // Vérifier si l'utilisateur est tenant owner
  const isTenantOwner = useMemo(() => {
    return user?.role === 'OWNER' || user?.role === 'SUPERADMIN';
  }, [user]);

  // Vérifier si l'utilisateur est admin (tenant ou company)
  const isAdmin = useMemo(() => {
    return (
      user?.role === 'OWNER' ||
      user?.role === 'ADMIN' ||
      user?.role === 'SUPERADMIN' ||
      user?.isCompanyAdmin ||
      effectiveRole === 'OWNER' ||
      effectiveRole === 'ADMIN'
    );
  }, [user, effectiveRole]);

  /**
   * Vérifier une permission spécifique
   */
  const can = useCallback(
    (action: CompanyAction): boolean => {
      // Tenant OWNER a tous les droits
      if (isTenantOwner) {
        return true;
      }

      // Pas de rôle = pas d'accès
      if (!effectiveRole) {
        return false;
      }

      // Vérifier dans la matrice
      return PERMISSION_MATRIX[effectiveRole]?.includes(action) || false;
    },
    [effectiveRole, isTenantOwner]
  );

  /**
   * Vérifier si l'utilisateur a au moins une des permissions
   */
  const canAny = useCallback(
    (actions: CompanyAction[]): boolean => {
      return actions.some((action) => can(action));
    },
    [can]
  );

  /**
   * Vérifier si l'utilisateur a toutes les permissions
   */
  const canAll = useCallback(
    (actions: CompanyAction[]): boolean => {
      return actions.every((action) => can(action));
    },
    [can]
  );

  /**
   * Récupérer toutes les permissions de l'utilisateur
   */
  const permissions = useMemo<CompanyAction[]>(() => {
    if (isTenantOwner) {
      return PERMISSION_MATRIX.OWNER;
    }
    if (!effectiveRole) {
      return [];
    }
    return PERMISSION_MATRIX[effectiveRole] || [];
  }, [effectiveRole, isTenantOwner]);

  return {
    // État
    companyRole: effectiveRole,
    isTenantOwner,
    isAdmin,
    permissions,

    // Méthodes de vérification
    can,
    canAny,
    canAll,

    // Helpers pratiques
    canCreateInvoice: can('create:invoice'),
    canReadInvoice: can('read:invoice'),
    canUpdateInvoice: can('update:invoice'),
    canDeleteInvoice: can('delete:invoice'),
    canCreateQuote: can('create:quote'),
    canReadQuote: can('read:quote'),
    canUpdateQuote: can('update:quote'),
    canDeleteQuote: can('delete:quote'),
    canCreateClient: can('create:client'),
    canReadClient: can('read:client'),
    canUpdateClient: can('update:client'),
    canDeleteClient: can('delete:client'),
    canCreateProduct: can('create:product'),
    canReadProduct: can('read:product'),
    canUpdateProduct: can('update:product'),
    canDeleteProduct: can('delete:product'),
    canCreateExpense: can('create:expense'),
    canReadExpense: can('read:expense'),
    canUpdateExpense: can('update:expense'),
    canDeleteExpense: can('delete:expense'),
    canApproveExpense: can('approve:expense'),
    canReadAccounting: can('read:accounting'),
    canExportAccounting: can('export:accounting'),
    canManageVAT: can('manage:vat'),
    canReadTreasury: can('read:treasury'),
    canManageSettings: can('manage:settings'),
    canManageUsers: can('manage:users'),
    canManageCompany: can('manage:company'),
  };
}

/**
 * Composant Guard pour protéger des sections de l'UI
 */
interface PermissionGateProps {
  action: CompanyAction | CompanyAction[];
  mode?: 'all' | 'any';
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

export function PermissionGate({
  action,
  mode = 'any',
  fallback = null,
  children,
}: PermissionGateProps) {
  const { can, canAny, canAll } = usePermission();

  const hasPermission = useMemo(() => {
    if (Array.isArray(action)) {
      return mode === 'all' ? canAll(action) : canAny(action);
    }
    return can(action);
  }, [action, mode, can, canAny, canAll]);

  if (!hasPermission) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Export de la matrice pour référence
 */
export { PERMISSION_MATRIX };

export default usePermission;
